import React, { useState } from 'react';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import Input from '@src/components/ui2/Input';
import { authSelector } from '@src/features/customers';
import UserForm from '../UserForm';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { MdArrowForward, MdOutlineEmail } from 'react-icons/md';
import LoginForm from '../LoginForm';
import Button from '@src/components/ui2/Button';
import { AuthMessage, EmailDisplay } from './style';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schema';
import { AUTH_MODE, type IdentificationAuthMode } from '@src/domains/customers';
import { useCheckEmailAvailability } from '@src/features/customers/auth/identification';

type AuthFormProps = {
  editUser?: boolean;
  fillCustomerForm?: boolean;
  onLoginSuccess?: () => void;
};

const AuthForm = (props: AuthFormProps) => {
  const { editUser = false, fillCustomerForm = false, onLoginSuccess = () => {} } = props;

  const { i18n } = useTranslate();
  const { identify, loading: loadingCheckEmail } = useCheckEmailAvailability();

  const { logged, buyer } = useAppSelector(authSelector);

  const [email, setEmail] = useState<string>('');
  const [route, setRoute] = useState<IdentificationAuthMode | undefined>(undefined);

  const { handleSubmit, control, formState } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema(i18n)),
  });
  const { errors } = formState;

  const checkEmail = async (payload: FieldValues) => {
    const resolvedRoute = await identify(payload['email']);

    if (resolvedRoute) {
      setRoute(resolvedRoute);
      setEmail(payload['email']);
    }
  };

  return (
    <>
      {!logged && !email ? (
        <form onSubmit={handleSubmit(checkEmail)}>
          <AuthMessage>{i18n('direct_link_auth_message')}</AuthMessage>

          <Controller
            name='email'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='email'
                label={i18n('direct_link_auth_email_label')}
                name={name}
                value={value || ''}
                required
                onChange={onChange}
                error={!!errors[name]}
                disabled={loadingCheckEmail}
                helperText={errors[name]?.message as string}
                {...rest}
              />
            )}
          />

          <Button fullWidth type='submit' loading={loadingCheckEmail}>
            {i18n('direct_link_auth_continue')} <MdArrowForward />
          </Button>
        </form>
      ) : null}

      {!logged && email ? (
        <>
          <EmailDisplay>
            <MdOutlineEmail /> {email}
          </EmailDisplay>

          {route === AUTH_MODE.LOGIN ? <LoginForm email={email} onSuccess={onLoginSuccess} /> : null}

          {route === AUTH_MODE.SIGNUP ? <UserForm /> : null}
        </>
      ) : null}

      {logged && editUser ? <UserForm fillData={fillCustomerForm} /> : null}
    </>
  );
};

export default AuthForm;
